import { Link } from 'react-router-dom'
import { useLeague } from '../contexts/LeagueContext'
import { sectionEnabled } from '../contexts/tenantAccess'

interface VenueLinkProps {
  /** The leaf venue id (most specific — could be a root or a child). */
  venueId: number | null | undefined
  /** The leaf venue's raw name (e.g. "Court 1" or "Main Gym"). */
  venueName: string | null | undefined
  /**
   * When the leaf venue has a parent root (e.g. "Court 1"'s parent is "ICI"),
   * its id and name come through here. Both are null when the venue is a root.
   */
  parentVenueId?: number | null
  parentVenueName?: string | null
  className?: string
}

/**
 * Renders a venue label and (when venues are enabled) links to the *root*
 * venue's detail page. We never link to a child venue directly: the root is
 * the page, children are listed inside it.
 *
 * Display rule:
 *   - parent present  → "<venue_name> · <parent_venue_name>", link = root
 *   - parent absent   → "<venue_name>", link = the venue itself
 *   - missing name    → renders nothing
 *
 * `stopPropagation` on the link guards the common case where a venue name
 * sits inside a larger clickable game card — clicking the venue navigates to
 * the venue, not the game.
 */
export default function VenueLink({
  venueId,
  venueName,
  parentVenueId = null,
  parentVenueName = null,
  className,
}: VenueLinkProps) {
  const { league } = useLeague()

  if (!venueName) return null

  // The display label: child venues read as "Leaf · Parent"; roots read as
  // just their name.
  const hasParent = parentVenueId != null && !!parentVenueName
  const label = hasParent ? `${venueName} · ${parentVenueName}` : venueName

  // The link target is always the *root* venue — never a child id.
  const targetId = hasParent ? parentVenueId : venueId

  const venuesEnabled = !!league && sectionEnabled(league, 'venues')
  if (!venuesEnabled || targetId == null) {
    return <span className={className}>{label}</span>
  }

  return (
    <Link
      to={`/venues/${targetId}`}
      onClick={(e) => e.stopPropagation()}
      className={`${className ?? ''} hover:text-primary hover:underline underline-offset-2 transition-colors`.trim()}
    >
      {label}
    </Link>
  )
}
